import { InteractionRequiredAuthError, type SilentRequest, type AccountInfo } from "@azure/msal-browser";
import { getMsalInstance, loginRequest } from "@/config/authConfig";
import { getEnvConfig } from "@/config/envConfig";

const ENV_CONFIG = getEnvConfig();

export async function getAccessTokenInfo() {
  const currentAccount = getMsalInstance().getActiveAccount();

  const silentRequest: SilentRequest = _createSilentRequest(loginRequest.scopes, currentAccount);

  try {
    return await getMsalInstance().acquireTokenSilent(silentRequest);
  } catch (error) {
    if (
      error instanceof InteractionRequiredAuthError ||
      error.error === "invalid_grant" ||
      error.suberror === "bad_token" ||
      currentAccount === null
    ) {
      console.warn("Refresh token expired. Redirecting to login...");
      return getMsalInstance().loginRedirect(loginRequest);
    } else {
      console.debug("Silent token acquisition failed. Error: " + JSON.stringify(error));
      throw error;
    }
  }
}

export async function logout() {
  const currentAccount = getMsalInstance().getActiveAccount();
  const logoutRedirectUrl = ENV_CONFIG.authLogoutRedirectUri;
  await getMsalInstance().logoutRedirect({ account: currentAccount, postLogoutRedirectUri: logoutRedirectUrl });
}

export async function getApiTokenInfo() {
  const currentAccount = getMsalInstance().getActiveAccount();

  const silentRequest: SilentRequest = _createSilentRequest(ENV_CONFIG.scopes, currentAccount);

  try {
    return await getMsalInstance().acquireTokenSilent(silentRequest);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return _acquireTokenRedirect(ENV_CONFIG.scopes, currentAccount);
    }
  }
}

export async function getClientTokenInfo() {
  const currentAccount = getMsalInstance().getActiveAccount();

  const silentRequest: SilentRequest = _createSilentRequest(ENV_CONFIG.appScope, currentAccount);

  console.debug("current Account-- ", currentAccount, " Silent Request: ", silentRequest);
  try {
    return await getMsalInstance().acquireTokenSilent(silentRequest);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return _acquireTokenRedirect(ENV_CONFIG.appScope, currentAccount);
    }
  }
}

export async function getCopilotStudioChannelToken(): Promise<string> {
  const scopes = getEnvConfig().copilotChannelScopes;
  if (!scopes?.length) {
    throw new Error("Copilot channel scopes are not configured");
  }
  const tokenResult = await _acquireCopilotToken(scopes);
  console.log("Copilot Studio channel token acquired: ", tokenResult?.accessToken, " for scopes: ", scopes.join(",  "));
  if (!tokenResult?.accessToken) {
    throw new Error("Unable to acquire Copilot Studio channel token");
  }
  return tokenResult.accessToken;
}

export async function getCopilotStudioSsoToken(scopeOverride?: string, forceRefresh = false): Promise<string> {
  const scope = scopeOverride?.trim() || getEnvConfig().copilotSsoScope;
  if (!scope) {
    throw new Error("Copilot SSO scope is not configured");
  }
  const tokenResult = await _acquireCopilotToken([scope], forceRefresh);
  if (!tokenResult?.accessToken) {
    throw new Error("Unable to acquire Copilot Studio SSO token");
  }
  return tokenResult.accessToken;
}

/** Interactive consent for Copilot SSO scope — used after silent token exchange returns 412. */
export async function getCopilotStudioSsoTokenInteractive(scopeOverride?: string): Promise<string> {
  const scope = scopeOverride?.trim() || getEnvConfig().copilotSsoScope;
  if (!scope) {
    throw new Error("Copilot SSO scope is not configured");
  }
  const currentAccount = getMsalInstance().getActiveAccount();
  const tokenResult = await getMsalInstance().acquireTokenPopup({
    scopes: [scope],
    account: currentAccount ?? undefined,
    loginHint: currentAccount?.username
  });
  if (!tokenResult?.accessToken) {
    throw new Error("Unable to acquire Copilot Studio SSO token interactively");
  }
  return tokenResult.accessToken;
}

async function _acquireCopilotToken(scopes: string[], forceRefresh = false) {
  const currentAccount = getMsalInstance().getActiveAccount();
  const silentRequest: SilentRequest = _createSilentRequest(scopes, currentAccount, forceRefresh);

  try {
    return await getMsalInstance().acquireTokenSilent(silentRequest);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return _acquireTokenRedirect(scopes, currentAccount);
    }
    throw error;
  }
}

function _createSilentRequest(
  requestScopes: any[] = [],
  currentAccount: AccountInfo | null = null,
  forceRefresh = false
) {
  const silentRequest: SilentRequest = {
    scopes: requestScopes,
    forceRefresh
  };

  if (currentAccount) {
    silentRequest.account = currentAccount;
  }

  return silentRequest;
}

function _acquireTokenRedirect(requestScopes: any[] = [], currentAccount: AccountInfo | null = null) {
  const request = {
    scopes: requestScopes,
    loginHint: currentAccount?.username
  };
  return getMsalInstance().acquireTokenRedirect(request);
}
